import { UmmeldungResponse } from '../../../../generated/model/ummeldung-response.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { UmmeldungService } from '../../../service/ummeldung/ummeldung.service';
import { computed, inject } from '@angular/core';
import { UmmeldungRequest } from '../../../../generated/model/ummeldung-request.model';
import { catchError, map, Observable, pipe, switchMap, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { withSessionStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { Router } from '@angular/router';
import { getUmmeldungWizardStartDataFromState } from '../models';
import { getAktuellerProzessSchrittFromUrl } from '../utils/prozessschritt.util';
import { Produktdaten } from '../../../../generated/model/produktdaten.model';
import { UmmeldungMetadata } from '../../../../generated/model/ummeldung-metadata.model';
import { withTreeShakableDevTools } from '../../../store/features/devTools.feature';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import { mapUmmeldungAdresseToAddress } from '../utils/ummedlung-mapper';
import { getZaehlpunktnummerShort } from '../../meter-point-change-request/meter-point-change-request.utils';
import ProduktEnum = Produktdaten.ProduktEnum;

interface UmmeldungState {
  activeUmmeldung: Ummeldung;
  metadata: UmmeldungMetadata;
  loading: boolean;
  error: HttpErrorResponse | null;
  resumeId: string;
}

const initialState: UmmeldungState = {
  activeUmmeldung: {
    status: null,
    ummeldungTyp: null,
    produktdaten: [
      {
        vertragsnummer: '',
        zaehlpunktnummer: '',
        produkt: ProduktEnum.STROM,
        adresse: {
          ort: '',
          plz: '',
          strasse: '',
          hausnummer: '',
          stiege: '',
          tuernummer: '',
        },
      },
    ],
    ummeldedatum: null,
    bisherigerKunde: null,
    neuerKunde: null,
    aktuellerProzessSchritt: null,
  },
  metadata: {
    letzterBearbeiter: {
      vorname: null,
      nachname: null,
      email: null,
    },
    letztesBearbeitungsDatum: null,
    erstellungsDatum: null,
  },
  loading: false,
  error: null,
  resumeId: null,
};

export const UmmeldungStore = signalStore(
  withTreeShakableDevTools('UmmeldungStore'),
  withState<UmmeldungState>(initialState),
  withStorageSync(
    { key: 'ummeldung', select: (state: UmmeldungState) => ({ activeUmmeldung: state.activeUmmeldung }) },
    withSessionStorage(),
  ),
  withMethods((store, service = inject(UmmeldungService), router = inject(Router)) => {
    return {
      getUmmeldungById(id: string): Observable<Ummeldung> {
        patchState(store, { loading: true });
        return service.getUmmeldung({ id }).pipe(
          tap((response: UmmeldungResponse) =>
            patchState(store, {
              activeUmmeldung: { id, ...response.ummeldung },
              metadata: response.ummeldungMetadata,
              loading: false,
              error: null,
            }),
          ),
          map(response => ({ id, ...response.ummeldung })),
          catchError((error: HttpErrorResponse) => {
            patchState(store, { activeUmmeldung: null, loading: false, error });
            return throwError(() => error);
          }),
        );
      },
      createUmmeldung(ummeldungRequest: UmmeldungRequest): Observable<void> {
        patchState(store, { loading: true });
        return service.createUmmeldung({ ummeldungRequest }).pipe(
          tap((id: string) => patchState(store, { activeUmmeldung: { id, ...ummeldungRequest.ummeldung }, loading: false, error: null })),
          map(() => void 0),
          catchError((error: HttpErrorResponse) => this._handleError(error)),
        );
      },
      updateUmmeldung(id: string, ummeldungRequest: UmmeldungRequest): Observable<void> {
        patchState(store, { loading: true });
        return service.updateUmmeldung({ id, ummeldungRequest }).pipe(
          tap(() => patchState(store, { loading: false, error: null })),
          map(() => void 0),
          catchError((error: HttpErrorResponse) => this._handleError(error)),
        );
      },
      deleteUmmeldung(id: string): void {
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap(() =>
            service.deleteUmmeldung({ id }).pipe(
              tapResponse({
                next: _ => patchState(store, { loading: false, error: null }),
                error: (error: HttpErrorResponse) => patchState(store, { error, loading: false }),
              }),
            ),
          ),
        );
      },

      submitUmmeldung(id: string): void {
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap(() =>
            service.submitUmmeldung({ id }).pipe(
              tapResponse({
                next: _ => patchState(store, { loading: false, error: null }),
                error: (error: HttpErrorResponse) => patchState(store, { error, loading: false }),
              }),
            ),
          ),
        );
      },
      uploadUmmeldungPdf(id: string, file: File): void {
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap(() =>
            service.uploadUmmeldungPdf({ id, body: file }).pipe(
              tapResponse({
                next: _ => patchState(store, { loading: false, error: null }),
                error: (error: HttpErrorResponse) => patchState(store, { error, loading: false }),
              }),
            ),
          ),
        );
      },
      getUmmeldungPdf(id: string): Observable<Blob> {
        return service.getUmmeldungPdf({ id });
      },
      saveActiveUmmeldung(): Observable<void> {
        const aktuellerProzessSchritt = getAktuellerProzessSchrittFromUrl(router);
        const activeUmmeldung = store.activeUmmeldung();
        if (activeUmmeldung?.id) {
          return this.updateUmmeldung(activeUmmeldung.id, {
            ummeldung: { ...activeUmmeldung, status: Ummeldung.StatusEnum.BEGONNEN, aktuellerProzessSchritt: aktuellerProzessSchritt },
          });
        }
        return this.createUmmeldung({
          ummeldung: { ...activeUmmeldung, status: Ummeldung.StatusEnum.BEGONNEN, aktuellerProzessSchritt: aktuellerProzessSchritt },
        });
      },
      setActiveUmmeldung(ummeldung: Partial<Ummeldung>): void {
        patchState(store, { activeUmmeldung: { ...ummeldung } as Ummeldung });
      },
      updateActiveUmmeldung(partial: Partial<Ummeldung>) {
        const activeUmmeldung = store.activeUmmeldung();
        if (activeUmmeldung) {
          patchState(store, { activeUmmeldung: { ...activeUmmeldung, ...partial } });
        }
      },
      setUmmeldedatum(ummeldedatum: string | null): void {
        const activeUmmeldung = store.activeUmmeldung();

        if (activeUmmeldung) {
          patchState(store, { activeUmmeldung: { ...activeUmmeldung, ummeldedatum } });
        }
      },
      clearStore(): void {
        patchState(store, initialState);
        sessionStorage.removeItem('ummeldung');
      },
      reInitStore(): void {
        this._initializeStore(router);
      },
      _handleError(error: HttpErrorResponse) {
        patchState(store, { error, loading: false });
        return throwError(() => error);
      },
      async _initializeStore(): Promise<void> {
        const startData = getUmmeldungWizardStartDataFromState(router.currentNavigation()?.extras.state ?? history.state);
        const activeUmmeldung = store.activeUmmeldung();

        const resumeId = startData?.editableUmmeldungId ?? activeUmmeldung?.id;
        if (resumeId) {
          patchState(store, { resumeId: resumeId });
          return;
        }

        if (startData?.produktdaten?.length) {
          this.setActiveUmmeldung({ produktdaten: startData.produktdaten });
        }
      },
    };
  }),
  withHooks(store => ({
    onInit() {
      store._initializeStore();
    },
  })),
  withComputed(store => ({
    zaehlpunktnummerShort: computed(() => getZaehlpunktnummerShort(store.activeUmmeldung()?.produktdaten.at(0)?.zaehlpunktnummer)),
    adresseFormatted: computed(() =>
      getAddressStreetAndPostCodeFormatted(mapUmmeldungAdresseToAddress(store.activeUmmeldung()?.produktdaten.at(0)?.adresse)),
    ),
  })),
);
